import React from 'react';
import { ProcessingConfig } from '../../types/create';
import { LayoutList } from 'lucide-react';

interface ProcessingConfigurationProps {
  processingConfig: ProcessingConfig | null;
  isLoadingConfig: boolean;
  onUpdateProcessingRule: (ruleId: string, enabled: boolean) => void;
  onUpdateSegmentationConfig: (field: string, value: string | number) => void;
  onUpdateIndexingMethod: (method: string) => void;
  onPreviewChunk: () => void;
  isLoadingPreview: boolean;
  canPreview: boolean;
}

const ProcessingConfiguration: React.FC<ProcessingConfigurationProps> = ({
  processingConfig,
  isLoadingConfig,
  onUpdateProcessingRule,
  onUpdateSegmentationConfig,
  onUpdateIndexingMethod,
  onPreviewChunk,
  isLoadingPreview,
  canPreview
}) => {
  if (isLoadingConfig) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Loading configuration...</p>
      </div>
    );
  }

  if (!processingConfig) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-sm text-gray-600">Failed to load configuration. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chunk Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Chunk Settings</h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">🔧</span>
            </div>
            <span className="text-sm font-medium">General</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">General text chunking mode, the chunks retrieved and recalled are the same.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold mb-1">
              Delimiter
              {/* <span className="text-gray-400">ℹ</span> */}
            </label>
            <input
              type="text"
              value={processingConfig.rules.segmentation.delimiter}
              onChange={(e) => onUpdateSegmentationConfig('delimiter', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="\n"
            />
          </div>

          <div>
            <label className="block text-xs font-bold mb-1">
              Maximum chunk length
              {/* <span className="text-gray-400">ℹ</span> */}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={processingConfig.rules.segmentation.maxTokens}
                onChange={(e) => onUpdateSegmentationConfig('max_tokens', parseInt(e.target.value))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold mb-1">
              Chunk overlap
              {/* <span className="text-gray-400 border rounded-full">?</span> */}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={processingConfig.rules.segmentation.chunkOverlap}
                onChange={(e) => onUpdateSegmentationConfig('chunk_overlap', parseInt(e.target.value))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </div>

        {/* Text Pre-processing Rules */}
        <div>
          <h4 className="text-xs font-bold mb-2">Text Pre-processing Rules</h4>
          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={processingConfig.rules.preProcessingRules.find(r => r.id === 'remove_extra_spaces')?.enabled || false}
                onChange={(e) => onUpdateProcessingRule('remove_extra_spaces', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Replace consecutive spaces, newlines and tabs</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={processingConfig.rules.preProcessingRules.find(r => r.id === 'remove_urls_emails')?.enabled || false}
                onChange={(e) => onUpdateProcessingRule('remove_urls_emails', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Remove URLs and email addresses</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={processingConfig.rules.preProcessingRules.find(r => r.id === 'qa_generation')?.enabled || false}
                onChange={(e) => onUpdateProcessingRule('qa_generation', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Q&A Generation</span>
            </label>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={onPreviewChunk}
            disabled={!canPreview || isLoadingPreview}
            className="flex items-center space-x-1 px-3 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>🔍</span>
            <span>{isLoadingPreview ? 'Loading...' : 'Preview Chunk'}</span>
          </button>
          <button className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800">
            Reset
          </button>
        </div>
      </div>

      {/* Parent-child Mode Info */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs">⚠</span>
          </div>
          <span className="text-sm font-medium text-gray-900">Parent-child</span>
        </div>
        <p className="text-xs text-gray-600">When using the parent-child mode, the child-chunk is used for retrieval and the parent-chunk is used for recall as context.</p>
      </div>

      {/* Index Method */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Index Method</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div
            onClick={() => onUpdateIndexingMethod('high_quality')}
            className={`border-2 rounded-lg p-3 cursor-pointer transition-all duration-200 ${processingConfig.mode === 'high_quality'
              ? 'border-orange-500 bg-orange-50'
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
              <span className="text-sm font-medium">High Quality</span>
              <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded">RECOMMEND</span>
            </div>
            <p className="text-xs text-gray-600">Calling the embedding model to process documents for more precise retrieval helps LLM generate high-quality answers.</p>
          </div>

          <div
            onClick={() => onUpdateIndexingMethod('economical')}
            className={`border-2 rounded-lg p-3 cursor-pointer transition-all duration-200 ${processingConfig.mode === 'economical'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
              <span className="text-sm font-medium">Economical</span>
            </div>
            <p className="text-xs text-gray-600">Using 10 keywords per chunk for retrieval, no tokens are consumed at the expense of reduced retrieval accuracy.</p>
          </div>
        </div>
      </div>

      {/* Retrieval Setting */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900">Retrieval Setting</h3>
        <p className="text-xs text-gray-600 mb-3">Learn more about retrieval method you can change this at any time in the Knowledge settings</p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs"><LayoutList /></span>
            </div>
            <span className="text-sm font-medium text-gray-900">Inverted Index</span>
          </div>
          <p className="text-xs text-gray-600">Inverted index is a structure used for efficient retrieval. Organized by terms, each term points to documents or web pages containing it.</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold mb-1">
              Top K
              {/* <span className="text-gray-400">ℹ️</span> */}
            </label>
            <input
              type="number"
              defaultValue={3}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
          <div></div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingConfiguration;
